/**
 * Generate a JPEG poster image from an MP4 file using an off-DOM video and canvas.
 * @param {File} file
 * @returns {Promise<{ posterFile: File, previewUrl: string, width: number, height: number }>}
 */
const generateVideoPoster = file => new Promise( ( resolve, reject ) => {
    if ( !( file instanceof File ) ) {
        reject( new Error( "Invalid video file." ) );
        return;
    }

    const objectUrl = URL.createObjectURL( file );
    const video = document.createElement( "video" );
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = "anonymous";
    video.src = objectUrl;

    let settled = false;

    const cleanup = () => {
        video.pause();
        video.removeAttribute( "src" );
        video.load();
        URL.revokeObjectURL( objectUrl );
    };

    const fail = error => {
        if ( settled ) return;
        settled = true;
        cleanup();
        reject( error instanceof Error ? error : new Error( String( error ) ) );
    };

    const capture = () => {
        if ( settled ) return;

        const width = video.videoWidth;
        const height = video.videoHeight;
        if ( width < 1 || height < 1 ) {
            fail( new Error( "Video metadata is unavailable." ) );
            return;
        }

        const canvas = document.createElement( "canvas" );
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext( "2d" );
        if ( context === null ) {
            fail( new Error( "Canvas rendering is unavailable." ) );
            return;
        }

        context.drawImage( video, 0, 0, width, height );
        canvas.toBlob( blob => {
            if ( settled ) return;
            if ( !( blob instanceof Blob ) ) {
                fail( new Error( "Unable to generate a poster image." ) );
                return;
            }

            const basename = file.name.replace( /\.[^.]+$/, "" ) || "video";
            const posterFile = new File( [ blob ], `${ basename }-poster.jpg`, { type: "image/jpeg" } );
            const previewUrl = URL.createObjectURL( posterFile );

            settled = true;
            cleanup();
            resolve( { posterFile, previewUrl, width, height } );
        }, "image/jpeg", 0.85 );
    };

    video.addEventListener( "error", () => fail( new Error( "Unable to load the selected video." ) ), { once: true } );
    video.addEventListener( "loadedmetadata", () => {
        const duration = Number.isFinite( video.duration ) ? video.duration : 0;
        const targetTime = duration > 1 ? 1 : 0;

        if ( targetTime === 0 ) {
            capture();
            return;
        }

        const onSeeked = () => {
            video.removeEventListener( "seeked", onSeeked );
            capture();
        };

        video.addEventListener( "seeked", onSeeked );
        try {
            video.currentTime = targetTime;
        } catch ( error ) {
            video.removeEventListener( "seeked", onSeeked );
            capture();
        }
    }, { once: true } );
} );

export default generateVideoPoster;
